/**
 *
 * Copyright © 2017 Easymeeting AS. All rights reserved.
 *
 * This software is produced by Easymeeting AS.
 * Unauthorized redistribution, reproduction, or usage of
 * this software in whole or in part without the express
 * written consent of Easymeeting AS is strictly prohibited.
 */

import { mapActions, mapGetters } from 'vuex';
import searchMixin from '@/mixins/search';
import buttonScroll from '@/mixins/buttonScroll';
import { getAllDomains } from '@/api/domain';
import { resetPassword } from '@/api/profile';
import languageMixin from '@/mixins/language';
import validator from 'validator';
import 'url-search-params-polyfill';
export default {
  name: 'Landing',
  mixins: [buttonScroll, languageMixin, searchMixin],
  components: {},
  props: {},
  data () {
    return {
      domains: [],
      domainSearchInput: null,
      forgotEmail: null,
      forgotPasswordPopup: false,
      hasValidationError: false,
      isLoggingIn: false,
      loginFailed: false,
      mousemoveTimeout: null,
      onQRHelp: false,
      onSearchScreen: false,
      onSignIn: true,
      password: null,
      rememberMe: false,
      requestingReset: false,
      showSearchDomain: false,
      username: null
    };
  },
  computed: {
    ...mapGetters('ContactStore', ['searchResults']),
    ...mapGetters('AppStore', {
      $_CONFIG: 'config',
      bandwidth: 'bandwidth',
      isOauthEnabled: 'isOauthEnabled',
      isDesktopApp: 'isDesktopApp'
    }),
    ...mapGetters('AuthStore', {
      user: 'profile',
      rememberedUsername: 'rememberedUsername',
      rememberedPassword: 'rememberedPassword'
    }),
    signInWithText () {
      return this.isOauthEnabled
        ? `${this.$t('APP_TEXT_SIGNIN_WITH')} ${this.$_CONFIG.appconfig.OAUTH_CONFIG.PROVIDER_NAME || ''}`
        : '';
    },
    defaultDomain () {
      return this.domains.find(elem => elem.isdefault === '1');
    },
    domainSearch () {
      return (this.domainSearchInput || '').toLowerCase().trim();
    },
    sortedDomains () {
      return this.domains.filter(elem => {
        return elem.domainid !== this.$_CONFIG.appconfig.DOMAIN_ID && elem.appurl && elem.appurl.trim().length > 5;
      }).sort((a, b) => {
        return a.domainname < b.domainname;
      }) || [];
    },
    filteredDomains () {
      return [
        ...this.sortedDomains.filter(elem => {
          if (this.domainSearch.length > 0) {
            return elem.domainname.toLowerCase().startsWith(this.domainSearch, 0);
          }
          return true;
        }),
        ...this.sortedDomains.filter(elem => {
          if (this.domainSearch.length > 0) {
            return elem.domainname.toLowerCase().search(this.domainSearch) > 0;
          }
          return false;
        })
      ];
    }
  },
  directives: {
    imageloded: {
      inserted (el, binding) {
        el.onerror = () => {
          binding.value.hasImage = false;
        };
        el.onload = () => {
          binding.value.hasImage = true;
        };
      }
    }
  },
  created () {
    if (this.$route.params.desktop === 'desktop') {
      if (!this.$route.query.signin) {
        this.onGoBack();
      }
    }
    this.$store.subscribe((mutation, state) => {
      switch (mutation.type) {
        case 'AppStore/setIsDesktopApp':
          this.desktopAppSetup(state.isDesktopApp);
          break;
      }
    });
    this.subscribeLoginActions();
    this.fetchDomains();
  },
  mounted () {
    this.desktopAppSetup(this.isDesktopApp);
    this.setRememberedCredentials();
    this.$eventBus.$emit('appReady');
  },
  beforeDestroy () {
    this.unsubscribeLoginActions();
  },
  methods: {
    ...mapActions('AuthStore', {
      setRememberedUsername: 'setRememberedUsername',
      setRememberedPassword: 'setRememberedPassword'
    }),
    desktopAppSetup (status) {
      if (status) {
        if (!this.$route.query.signin) {
          this.onGoBack();
        }
      }
    },
    clearForgotEmail () {
      this.forgotEmail = null;
    },
    clearPassword () {
      this.password = null;
    },
    clearDomainSearch () {
      this.domainSearchInput = null;
    },
    clearUsername () {
      this.username = null;
    },
    domainChange (mouseMovement, fromControl = false) {
      if (!fromControl) {
        this.$eventBus.$emit('sendToControls', {
          type: 'task',
          scope: 'landing',
          data: {
            task: 'domainChange'
          }
        });
      }
      this.$router.push({ name: 'domain' });
    },
    async fetchDomains () {
      try {
        this.domains = (await getAllDomains()).map((el) => { return { ...el, hasImage: !!el.BRAND_LOGO_ICON }; });
      } catch (error) {
        this.domains = [];
      }
    },
    getActiveContainer () {
      return this.$refs.landingSearchResults;
    },
    gotoDomain (domain) {
      if (domain) {
        const appurl = `${domain.appurl}?signin=true`;
        if (this.isDesktopApp) {
          this.$eventBus.$emit('changeDomain', appurl);
        } else {
          window.location.replace(appurl);
        }
      }
    },
    gotoSignUp () {
      if (this.isDesktopApp) {
        this.$_NATIVE.openInBrowser(this.$_CONFIG.appconfig.SIGNUP_URL);
      } else {
        window.location.replace(this.$_CONFIG.appconfig.SIGNUP_URL);
      }
    },
    showSettingsPage () {

    },
    hideForgotPassword (mouseMovement, fromControl = false) {
      if (!fromControl) {
        this.$eventBus.$emit('sendToControls', {
          type: 'task',
          scope: 'landing',
          data: {
            task: 'hideForgotPassword'
          }
        });
      }
      this.forgotPasswordPopup = false;
      this.forgotEmail = null;
    },
    initializeCall (contact) {
      this.$eventBus.$emit('goto-webrtc-page', contact);
    },
    isValidPassword () {
      if (this.password) {
        return this.password.length > 0;
      }
      return false;
    },
    isValidUsername () {
      if (this.username) {
        this.username = this.username.trim();
        return this.username.length > 0;
      }
      return false;
    },
    login () {
      if (!this.isValidUsername()) {
        this.$eventBus.$emit('showToast', {
          iconLeft: 'SvgClose',
          text: this.$t('APP_TEXT_INVALID_USERNAME'),
          static: false,
          short: true
        });
      } else if (!this.isValidPassword()) {
        this.$eventBus.$emit('showToast', {
          iconLeft: 'SvgClose',
          text: this.$t('APP_TEXT_INVALID_PASSWORD'),
          static: false,
          short: true
        });
      } else {
        this.isLoggingIn = true;
        this.$eventBus.$emit('showToast', {
          iconLeft: 'SvgLoading',
          text: this.$t('APP_TEXT_AUTHENTICATING'),
          static: true
        });
        this.$refs.maskedLoginForm.submit({
          isOauth: false,
          username: this.username,
          password: this.password
        });
        this.setRememberedUsername(this.rememberMe ? this.username : null);
        this.setRememberedPassword(this.rememberMe ? this.password : null);
      }
    },
    loginWith () {
      if (this.isDesktopApp) {
        this.$_NATIVE.oauthLogin(this.$_CONFIG.appconfig.OAUTH_CONFIG);
      } else {
        window.location.replace(this.$_CONFIG.appconfig.OAUTH_CONFIG.AUTHORIZATION_ENDPOINT_WEB);
      }
    },
    onAuthenticateUser (data) {
      const authToken = data;
      if (authToken) {
        this.isLoggingIn = true;
        this.$eventBus.$emit('showToast', {
          iconLeft: 'SvgLoading',
          text: this.$t('APP_TEXT_AUTHENTICATING'),
          static: true
        });
        this.$refs.maskedLoginForm.submit({
          isOauth: true,
          authToken
        });
      }
    },
    onBandWidthChange (newValue) {},
    onLoginError (info) {
      this.resetLoginFrame();
      this.isLoggingIn = false;
      this.$eventBus.$emit('hideToast');
      this.loginFailed = true;
      // this.$eventBus.$emit('showToast', {
      //   iconLeft: 'SvgClose',
      //   text: this.$t('APP_TEXT_AUTHENTICATION_FAILED'),
      //   static: false,
      //   short: true
      // });
    },
    async onLoginSuccess (info) {
      this.isLoggingIn = false;
      await this.$store.dispatch('AuthStore/loginSuccess', info, {
        root: true
      });
      this.$eventBus.$emit('hideToast');
      this.$eventBus.$emit('try-validation', false);
    },
    onMessageFromControl ({ message }) {
      try {
        if (message.scope === 'landing') {
          if (message.type === 'input') {
            switch (message.data.field) {
              case 'forgotEmail':
                this.forgotEmail = message.data.value;
                break;
              case 'hasValidationError':
                this.hasValidationError = message.data.value;
                break;
              case 'isLoggingIn':
                this.isLoggingIn = message.data.value;
                break;
              case 'password':
                this.password = message.data.value;
                break;
              case 'requestingReset':
                this.requestingReset = message.data.value;
                break;
              case 'username':
                this.username = message.data.value;
                break;
            }
          } else if (message.type === 'task') {
            switch (message.data.task) {
              case 'clearForgotEmail':
                this.clearForgotEmail();
                break;
              case 'clearPassword':
                this.clearPassword();
                break;
              case 'clearUsername':
                this.clearUsername();
                break;
              case 'domainChange':
                this.domainChange(null, true);
                break;
              case 'hideForgotPassword':
                this.hideForgotPassword(null, true);
                break;
              case 'login':
                this.login();
                break;
              case 'resetPassword':
                this.resetPassword();
                break;
              case 'showForgotPassword':
                this.showForgotPassword(null, true);
                break;
            }
          }
        }
      } catch (err) {}
    },
    onQRGotToken (token) {
      this.onLoginSuccess({ token });
    },
    onGoBack () {
      if (this.forgotPasswordPopup) {
        this.hideForgotPassword();
      } else if (this.showSearchDomain) {
        this.showSearchDomain = false;
        this.domainSearchInput = null;
        if (this.$refs.domainSearchInputField) {
          this.$refs.domainSearchInputField.inputFocusOut();
        }
      } else if (this.onSignIn) {
        this.onSignIn = false;
      } else if (this.onSearchScreen) {
        this.onSearchScreen = false;
        this.clearSearch();
      } else {
        this.$_NATIVE.exitApp();
      }
    },
    onResetDomain () {
      if (this.$_CONFIG.appconfig.is_default_domain) {
        this.onShowDomainSearch();
      } else {
        this.gotoDomain(this.defaultDomain);
      }
    },
    onShowDomainSearch () {
      this.showSearchDomain = true;
      this.$nextTick(() => {
        if (this.$refs.domainSearchInputField) {
          this.$refs.domainSearchInputField.inputFocusIn();
        }
      });
    },
    openTermsOfService () {
      if (this.isDesktopApp) {
        this.$_NATIVE.openInBrowser(this.$_CONFIG.appconfig.TERMS_OF_SERVICE_URL);
      } else {
        window.open(this.$_CONFIG.appconfig.TERMS_OF_SERVICE_URL, '_blank');
      }
    },
    resetLoginFrame () {
      this.$refs.maskedLoginForm.reset();
    },
    async resetPassword () {
      if (this.forgotEmail && validator.isEmail(this.forgotEmail)) {
        let result = null;
        this.requestingReset = true;
        try {
          result = await resetPassword(this.forgotEmail);
        } catch (error) {}
        if (result) {
          this.$eventBus.$emit('showToast', {
            iconLeft: 'SvgTick',
            text: this.$t('APP_TEXT_PASSWORD_RESET_SUCCESS'),
            static: false,
            short: true
          });
          const self = this;
          setTimeout(() => {
            self.hideForgotPassword();
          }, 5000);
        } else {
          this.$eventBus.$emit('showToast', {
            iconLeft: 'SvgClose',
            text: this.$t('APP_TEXT_PASSWORD_RESET_FAILED'),
            static: false,
            short: true
          });
        }
        this.requestingReset = false;
      } else {
        this.$eventBus.$emit('showToast', {
          iconLeft: 'SvgClose',
          text: this.$t('APP_TEXT_INVALID_EMAIL'),
          static: false,
          short: true
        });
      }
    },
    setRememberedCredentials () {
      if (this.rememberedUsername) {
        this.username = this.rememberedUsername;
        this.rememberMe = true;
      }
      if (this.rememberedPassword) {
        this.password = this.rememberedPassword;
        this.rememberMe = true;
      }
    },
    showContactInfo (contact) {
      this.$eventBus.$emit('show-contact-info', contact);
    },
    showForgotPassword (mouseMovement, fromControl = false) {
      if (!fromControl) {
        this.$eventBus.$emit('sendToControls', {
          type: 'task',
          scope: 'landing',
          data: {
            task: 'showForgotPassword'
          }
        });
      }
      this.forgotPasswordPopup = true;
      this.$nextTick(() => {
        if (this.$refs.forgotEmailInput) {
          this.$refs.forgotEmailInput.inputFocusIn();
        }
      });
    },
    showPairingScreen () {
      this.$eventBus.$emit('show-pairing-screen');
    },
    showQRHelp () {
      this.onQRHelp = !this.onQRHelp;
    },
    showSignIn () {
      this.onSignIn = true;
    },
    showSearchScreen () {
      this.onSearchScreen = true;
      this.$nextTick(() => {
        if (this.$refs.contactSearchInputField) {
          this.$refs.contactSearchInputField.inputFocusIn();
        }
      });
    },
    subscribeLoginActions () {
      this.$eventBus.$on('window-loginSuccess', this.onLoginSuccess);
      this.$eventBus.$on('window-loginError', this.onLoginError);
      this.$eventBus.$on('native-onAuthenticateUser', this.onAuthenticateUser);
    },
    trySigninAgan () {
      this.loginFailed = false;
    },
    unsubscribeLoginActions () {
      this.$eventBus.$off('window-loginSuccess', this.onLoginSuccess);
      this.$eventBus.$off('window-loginError', this.onLoginError);
      this.$eventBus.$off('native-onAuthenticateUser', this.onAuthenticateUser);
    }
  }
};
